/*** IMPORTS FROM imports-loader ***/
var fetchPolyfill = require("polyfills/fetch");
var classListPolyfill = require("polyfills/classList");

import _ from "underscore";
import { emptyElement } from "dom/manipulation";
import Matte from "./matte";
import alerts from "./alerts";
import globalEvents from "./global-events";
import BaseOverlay from "../components/base-overlay";
import globals from "../modules/globals";
import requester from "../modules/requester";

var matte;
var currentOverlay;
var defaultOptions = {
    content: null,
    url: null,
    cssClasses: ["size-xl"],
    onRenderFinish: _.noop,
    onOverlayClose: _.noop,
    force: false
};

/**
 * Initialize the overlay and parses the options
 * @options {
 *   content: A string, jQuery object or HTML element
 *   url: url to get content from. If set content will be ignored
 *   cssClasses: css class for the overlay with,
 *   onRenderFinish: callback function
 *   onOverlayClose : callback function
 *   blocked: should the layer be closable?
 * }
 */
function Overlay(options) {
    this.options = _.extend({}, defaultOptions, options);

    // allow a string to be passed but convert to an array
    if (_.isString(this.options.cssClasses)) {
        this.options.cssClasses = this.options.cssClasses.trim().split(" ");
    }

    if (this.options.blocked) {
        this.options.cssClasses.push("blocked");
    }

    if (this.options.force) {
        this.init();
    } else {
        this.throttledInit();
    }
}

Overlay.prototype = {
    /**
     * Shows matte, creates container element and checks if overlay has to load the content
     * It's throttled so that we don't get any madness.
     */
    init: function() {
        // If there is a current overlay remove or replace it
        if (currentOverlay) {
            currentOverlay.removeCurrentOverlay();
        } else {
            matte = new Matte({
                blocked: this.options.blocked
            });
            matte.open();
        }

        currentOverlay = this;

        this.checkIfContent();
    },

    throttledInit: _.throttle(
        function() {
            return this.init();
        },
        100,
        { trailing: false }
    ),

    /**
     * Checks if the content has to be load via AJAX
     */
    checkIfContent: function() {
        if (this.options.url) {
            matte.showLoading();
            this.getContent();
        } else {
            this.open();
        }
    },

    /**
     * Renders content into container and fades in the container
     */
    render: function() {
        this.overlayComponent.setContent(this.options.content);

        const container = matte.removeLoading().container;

        emptyElement(container);

        this.overlayComponent.appendTo(container);
        this.options.onRenderFinish.call(this, this);
    },

    /**
     * Handles a success response from the AJAX call that fetches the content.
     * @param {String} response Response from the server.
     */
    onRequestSuccess: function(response) {
        try {
            var json = JSON.parse(response);
            if (json && json.data && json.data.redirect) {
                globals.window.location = response.data.redirect;
                return;
            }
        } catch (e) {
            // this is fine.
        }

        this.options.content = response;
        this.open();
    },

    /**
     * Handles an error from the AJAX call that fetches the content.
     */
    onRequestError: function(err) {
        matte.close();

        // the user is logged out - best way to handle this for now is to reload the page
        if (err && err.status === 403) {
            alerts.loginRequiredThenRedirect();
            return;
        }

        alerts.alert("Something went wrong. Please try again");
    },

    /**
     * Fires get request to get content
     */
    getContent: function() {
        return requester(this.options.url, {
            headers: {
                Accept: "text/html"
            }
        })
            .then(requester.toText)
            .then(this.onRequestSuccess.bind(this))
            .catch(this.onRequestError.bind(this));
    },

    /**
     * Fades in the overlay
     */
    open: function() {
        // we need the component DOM element to be a component so that when it is
        // removed the destroy handlers are called on all it's children. At some
        // point we should make this whole overlay thing a component.
        this.overlayComponent = new BaseOverlay();

        this.options.cssClasses.forEach(c => this.overlayComponent.el.classList.add(c));

        this.overlayComponent.registerEvent("click", ".overlay-close", this.close.bind(this, true));

        globalEvents.on("overlay.close", this.close.bind(this, false));

        this.render();
    },

    /**
     * Closes the current overlay without closing the matte
     */
    removeCurrentOverlay: function(userInitiated) {
        if (this.overlayComponent) {
            this.overlayComponent.destroy();
            this.overlayComponent = null;
        }

        if (currentOverlay) {
            this.options.onOverlayClose({
                userInitiated: userInitiated || false
            });
            currentOverlay = null;
        }
    },

    /**
     * Hides the container
     */
    close: function(userInitiated, event) {
        if (event && _.isFunction(event.preventDefault)) {
            event.preventDefault();
        }

        this.removeCurrentOverlay(userInitiated);
        matte.close();

        globalEvents.off("overlay.close");
    }
};

globalEvents.on("matte.close", function() {
    if (currentOverlay) {
        currentOverlay.removeCurrentOverlay();
    }
});

export default Overlay;

